import React, { ReactElement, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import RingilSpinner from '@components/ringil3/RingilSpinner/RingilSpinner';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { CLICKABLE_TEXT_BASE } from '@components/RingilComponents/clickableText.ts';

interface ComponentProps {
  label?: string | JSX.Element;
  tooltip?: React.ReactNode;
  onClick: () => void;
  /**
   * Visual style. These are alternatives, not a generation gap — only `filled` is superseded.
   *
   *   `filled`             DEPRECATED, use `filled-rectangle` (the same button in the current shape)
   *   `outlined`           white, ORANGE border — the kit's "Outline" component
   *   `outlined` / `table` white, GREY border — the kit's "Table" component, for row actions
   *   `filled-rectangle`   brand fill, for the primary action
   *   `text-only`          borderless, compact — use inside dense rows and bars
   *   `link`               borderless, but a fixed 48px tall — for standalone links, not dense rows
   *
   * Picking between `outlined` and `outlined-rectangle` is a design question (orange vs grey border),
   * so check the frame rather than assuming the rectangle variant is the newer one.
   */
  mode?: 'outlined' | 'filled' | 'text-only' | 'table' | 'outlined-rectangle' | 'filled-rectangle' | 'link';
  size?: 'big' | 'small';
  iconPath?: string;
  icon?: ReactElement;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
  secured?: SecuredConfig;
}

const RingilButton: React.FC<ComponentProps> = ({ label, tooltip, onClick, iconPath, icon, mode = 'outlined', size = 'small', loading = false, disabled = false, className, dataTestId, secured }) => {
  const rootId = `button_${useId().replace(/:/g, '-')}`;
  const decoration = useSecuredDecoration(secured);
  // When a security rule supplies editability, it is authoritative; otherwise fall back to the disabled prop.
  const effectiveDisabled = decoration.editable === null ? disabled : !decoration.editable;
  const onlyIcon = (iconPath || icon) && !label;
  const isRectangleOutline = mode === 'outlined-rectangle' || mode === 'table';

  const buttonClassName = twMerge(
    clsx(
      // base
      'inline-flex items-center justify-center gap-1 !cursor-pointer rounded border-2 border-solid border-[color:var(--ringil3-brand-color)] font-medium',
      'disabled:!cursor-not-allowed disabled:opacity-50',
      // Size sets the height outright rather than letting padding and line-height add up to one.
      // The UI kit gives a size one height whatever it holds — small 32px, big 48px, with or
      // without an icon — and height-by-addition cannot honour that: a 16px icon is taller than the
      // 12px text line box, so an icon used to make a button 4px taller than its neighbour. Content
      // centres inside the fixed box instead. Padding overridden below for link / only-icon.
      // gap only shows when a button carries both an icon and a label. 6px rather than the kit's 8:
      // our icons keep their own padding inside the 16px box (download 2px a side, trash 1px, reset
      // none), so a literal 8px reads as 10px next to a padded glyph. 6px lands those around 8 and
      // leaves edge-to-edge glyphs tight rather than loose.
      size === 'small' ? 'h-8 gap-1.5 px-3 text-sm leading-3' : 'h-12 gap-1.5 px-6 py-4 text-base leading-normal tracking-[-0.16px]',

      mode === 'filled' && [
        'bg-[var(--ringil3-brand-color)] text-white',
        loading && 'cursor-auto border-[color:var(--ringil3-content-tertiary-color)] bg-[var(--ringil3-content-tertiary-color)]',
      ],

      mode === 'outlined' && [
        'bg-white text-[color:var(--ringil3-brand-color)]',
        loading && 'cursor-auto border-[color:var(--ringil3-content-tertiary-color)]',
      ],

      mode === 'filled-rectangle' && [
        'rounded-md border border-solid border-content-brand bg-content-brand text-white shadow-[0_0_16px_0_rgba(254,80,0,0.10)]',
        'disabled:border-content-tertiary disabled:bg-content-tertiary',
        'enabled:hover:[background:linear-gradient(0deg,rgba(0,0,0,0.10)_0%,rgba(0,0,0,0.10)_100%),#FE5000] enabled:hover:shadow-[0_0_16px_0_rgba(0,0,0,0.10)]',
      ],

      isRectangleOutline && [
        'order-0 flex-none grow-0 rounded-md border-2 border-solid border-content-tertiary bg-white text-content-brand shadow-[2px_2px_4px_0_rgba(254,80,0,0.15)] transition-all duration-200',
        'disabled:text-content-tertiary disabled:shadow-none',
        'enabled:hover:border-complementary-brandDark enabled:hover:text-complementary-brandDark',
      ],

      mode === 'text-only' && [
        'border-none bg-white text-content-brand transition-all duration-200',
        'disabled:text-content-tertiary',
        'enabled:hover:text-complementary-brandDark',
      ],

      mode === 'link' && [
        'order-0 flex-none grow-0 h-12 border-none bg-transparent p-4',
        // Same colour/hover rule as inline clickable text — defined once in clickableText.ts.
        CLICKABLE_TEXT_BASE,
        'disabled:text-content-tertiary',
        'enabled:hover:text-complementary-brandDark',
      ],

      // only-icon padding overrides (matches the original styled-component scope)
      onlyIcon && mode === 'link' && 'p-2.5',
      // Square by construction: width is pinned to the size's height and the icon is centred, so the
      // box cannot drift with border width the way padding arithmetic did (border-2 modes used to
      // come out 2px larger than border-1 ones).
      onlyIcon && (mode === 'filled-rectangle' || isRectangleOutline) && (size === 'big' ? 'w-12 p-0' : 'w-8 p-0'),
    ),
    className,
  );

  return (
    <>
      {decoration.decorate(
        <button onClick={() => (loading || effectiveDisabled ? {} : onClick())} disabled={effectiveDisabled} className={buttonClassName} id={rootId} data-testid={dataTestId}>
          <RingilSpinner isSpinning={loading} />
          {iconPath && <img src={iconPath} alt="" />}
          {icon && icon}
          {label && (
            <div className={tooltip ? 'cursor-help border-b border-l-0 border-r-0 border-t-0 border-dotted border-b-brand-color transition-all hover:border-solid' : undefined}>
              {label}
            </div>
          )}
          {tooltip && <CustomTooltip targetSelector={`#${rootId}`} content={tooltip} />}
        </button>,
      )}
    </>
  );
};

export default RingilButton;